import React, { useState } from 'react';
import adminApi from '../../api/client';

interface BulkRowDetail {
  row_index: number;
  row_data: Record<string, any>;
  reason: string;
}

interface ValidationResult {
  total_submitted: number;
  valid_count: number;
  duplicate_in_db_count: number;
  duplicate_in_payload_count: number;
  error_count: number;
  would_insert_count: number;
  errors: BulkRowDetail[];
  duplicates_in_db: BulkRowDetail[];
  duplicates_in_payload: BulkRowDetail[];
}

interface ImportResult {
  imported_count: number;
  skipped_count: number;
  total_after_import: number;
  message: string;
}

export default function BulkImportAuthors() {
  const [jsonText, setJsonText] = useState('');
  const [lastValidatedText, setLastValidatedText] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isTextEmpty = jsonText.trim().length === 0;
  const needsRevalidation = lastValidatedText === null || jsonText !== lastValidatedText;
  const canConfirm = !loading && validationResult !== null && !needsRevalidation && validationResult.would_insert_count > 0;

  const handleValidate = async () => {
    setError(null);
    setValidationResult(null);
    setImportResult(null);

    // Parse JSON
    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e: any) {
      const msg = e instanceof SyntaxError ? e.message : String(e);
      setError(`Invalid JSON: ${msg}`);
      console.error('Bulk import JSON parse error:', e);
      return;
    }

    if (!Array.isArray(parsed)) {
      setError('Expected a JSON array of author objects, e.g. [ { "name_en": "...", ... } ]');
      return;
    }

    if (parsed.length === 0) {
      setError('Array is empty — nothing to validate.');
      return;
    }

    setLoading(true);
    try {
      const res = await adminApi.post(
        '/admin/master/authors/bulk-validate',
        { rows: parsed, confirm: false },
        { timeout: 60000 }
      );
      setValidationResult(res.data);
      setLastValidatedText(jsonText);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Validation request failed';
      setError(detail);
      console.error('Bulk validate error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!validationResult) return;

    const proceed = window.confirm(
      `This will import ${validationResult.would_insert_count} new authors. Continue?`
    );
    if (!proceed) return;

    // Re-parse (textarea hasn't changed since validate, but parse fresh to be safe)
    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e: any) {
      setError(`Invalid JSON on re-parse: ${e instanceof SyntaxError ? e.message : String(e)}`);
      console.error('Bulk import re-parse error:', e);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.post(
        '/admin/master/authors/bulk-import',
        { rows: parsed, confirm: true },
        { timeout: 60000 }
      );
      setImportResult(res.data);
      setValidationResult(null);
      setJsonText('');
      setLastValidatedText(null);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Import request failed';
      setError(detail);
      console.error('Bulk import error:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderDetailTable = (title: string, items: BulkRowDetail[], colorClass: string) => {
    if (!items || items.length === 0) return null;
    return (
      <details className="mt-4">
        <summary className={`cursor-pointer font-semibold text-sm ${colorClass} select-none`}>
          {title} ({items.length})
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 border-b border-gray-200">
                <th className="p-3 font-semibold">Row #</th>
                <th className="p-3 font-semibold">Name EN</th>
                <th className="p-3 font-semibold">Name SI</th>
                <th className="p-3 font-semibold">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="p-3 text-gray-600">{item.row_index}</td>
                  <td className="p-3 text-gray-900">{item.row_data?.name_en || '—'}</td>
                  <td className="p-3 text-gray-900">{item.row_data?.name_si || '—'}</td>
                  <td className="p-3 text-gray-600">{item.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-bold text-[#085041] mb-2">Bulk Import Authors</h2>
      <p className="text-gray-500 text-sm mb-4">
        Paste a JSON array of author objects below. Click <strong>Validate</strong> to preview, then <strong>Confirm Import</strong> to write to the database.
      </p>

      {/* Textarea */}
      <textarea
        className="w-full border border-gray-300 rounded p-3 font-mono text-sm focus:outline-none focus:border-[#1D9E75] resize-y"
        rows={12}
        placeholder={`[\n  { "name_en": "Martin Wickramasinghe", "name_si": "මාර්ටින් වික්‍රමසිංහ", "is_translator": false, "is_verified": true },\n  { "name_en": "Mahagama Sekara", "name_si": "මහගම සේකර", "is_translator": false, "is_verified": true }\n]`}
        value={jsonText}
        onChange={(e) => setJsonText(e.target.value)}
        disabled={loading}
      />

      {/* Action buttons */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={handleValidate}
          disabled={isTextEmpty || loading}
          className="px-5 py-2 bg-[#085041] text-white rounded font-medium hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading && !validationResult ? '🔄 Validating…' : '🔍 Validate'}
        </button>
        <button
          onClick={handleConfirmImport}
          disabled={!canConfirm}
          className="px-5 py-2 bg-[#1D9E75] text-white rounded font-medium hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading && validationResult ? '🔄 Importing…' : '✅ Confirm Import'}
        </button>
      </div>

      {/* Re-validation warning */}
      {validationResult && needsRevalidation && (
        <div className="mt-3 bg-[#BA7517]/10 border border-[#BA7517] text-[#BA7517] px-4 py-2 rounded text-sm">
          ⚠️ Textarea content has changed since last validation. Please re-validate before importing.
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mt-4 bg-[#D85A30]/10 border border-[#D85A30] text-[#D85A30] px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Import success result */}
      {importResult && (
        <div className="mt-6 bg-[#1D9E75]/10 border border-[#1D9E75] text-[#085041] px-4 py-4 rounded">
          <div className="font-bold text-lg mb-2">✅ Import Complete</div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Imported:</span>{' '}
              <span className="font-bold text-[#1D9E75]">{importResult.imported_count}</span>
            </div>
            <div>
              <span className="text-gray-500">Skipped:</span>{' '}
              <span className="font-bold text-[#BA7517]">{importResult.skipped_count}</span>
            </div>
            <div>
              <span className="text-gray-500">Total Authors Now:</span>{' '}
              <span className="font-bold">{importResult.total_after_import}</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2">{importResult.message}</p>
        </div>
      )}

      {/* Validation result summary */}
      {validationResult && !needsRevalidation && (
        <div className="mt-6">
          <h3 className="text-lg font-bold text-[#085041] mb-3">Validation Preview</h3>

          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-gray-50 rounded p-3 text-center border border-gray-200">
              <div className="text-2xl font-bold text-gray-800">{validationResult.total_submitted}</div>
              <div className="text-xs text-gray-500 mt-1">Total Submitted</div>
            </div>
            <div className="bg-[#1D9E75]/10 rounded p-3 text-center border border-[#1D9E75]/30">
              <div className="text-2xl font-bold text-[#1D9E75]">{validationResult.would_insert_count}</div>
              <div className="text-xs text-gray-500 mt-1">Would Insert</div>
            </div>
            <div className="bg-[#BA7517]/10 rounded p-3 text-center border border-[#BA7517]/30">
              <div className="text-2xl font-bold text-[#BA7517]">{validationResult.duplicate_in_db_count}</div>
              <div className="text-xs text-gray-500 mt-1">Duplicates in DB</div>
            </div>
            <div className="bg-[#BA7517]/10 rounded p-3 text-center border border-[#BA7517]/30">
              <div className="text-2xl font-bold text-[#BA7517]">{validationResult.duplicate_in_payload_count}</div>
              <div className="text-xs text-gray-500 mt-1">Duplicates in Payload</div>
            </div>
            <div className="bg-[#D85A30]/10 rounded p-3 text-center border border-[#D85A30]/30">
              <div className="text-2xl font-bold text-[#D85A30]">{validationResult.error_count}</div>
              <div className="text-xs text-gray-500 mt-1">Errors</div>
            </div>
          </div>

          {/* Detail tables */}
          {renderDetailTable('Errors', validationResult.errors, 'text-[#D85A30]')}
          {renderDetailTable('Duplicates in DB', validationResult.duplicates_in_db, 'text-[#BA7517]')}
          {renderDetailTable('Duplicates in Payload', validationResult.duplicates_in_payload, 'text-[#BA7517]')}

          {validationResult.errors.length === 0 && validationResult.duplicates_in_db.length === 0 && validationResult.duplicates_in_payload.length === 0 && (
            <p className="text-sm text-[#1D9E75] mt-2">✓ All rows are valid and ready to import.</p>
          )}
        </div>
      )}
    </div>
  );
}
